/**
 * ShapeDetailPanel - Sidebar showing selected shape details + full shape list
 *
 * Top: selected shape properties (name, type, size, confidence, delete)
 * Bottom: scrollable list of all shapes with checkboxes and click-to-select
 */

import { Check, Trash } from '@phosphor-icons/react';
import styles from './FloorPlanEditor.module.css';

const STATUS_COLORS = {
  new: '#22c55e',
  matched: '#3b82f6',
  size_change: '#f59e0b',
};

function ConfidenceBadge({ confidence }) {
  const pct = Math.round(confidence * 100);
  const cls =
    confidence >= 0.8
      ? styles.confidenceHigh
      : confidence >= 0.6
        ? styles.confidenceMedium
        : styles.confidenceLow;
  return <span className={cls}>{pct}%</span>;
}

export default function ShapeDetailPanel({
  selectedShape,
  allShapes,
  masterSpaces,
  onUpdateShape,
  onDeleteShape,
  onSelectShape,
  onToggleInclude,
}) {
  const selectedData = selectedShape?.spaceData;

  const includedCount = allShapes.filter(s => s.spaceData?.included).length;

  return (
    <div className={styles.sidebar}>
      {/* Selected shape detail */}
      {selectedData ? (
        <div className={styles.detailPanel}>
          <div className={styles.detailTitle}>פרטי חלל</div>

          <div className={styles.detailField}>
            <div className={styles.detailLabel}>שם</div>
            <input
              className={styles.detailInput}
              value={selectedData.suggested_name_he || ''}
              onChange={e => {
                selectedData.suggested_name_he = e.target.value;
                onUpdateShape?.(selectedShape);
              }}
              dir="rtl"
            />
          </div>

          <div className={styles.detailField}>
            <div className={styles.detailLabel}>סוג חלל</div>
            <select
              className={styles.detailSelect}
              value={selectedData.master_space_id || ''}
              onChange={e => {
                const ms = masterSpaces?.find(s => s.id === e.target.value);
                selectedData.master_space_id = e.target.value || null;
                if (ms) {
                  selectedData.suggested_type = ms.name;
                }
                onUpdateShape?.(selectedShape);
              }}
              dir="rtl"
            >
              <option value="">בחר סוג...</option>
              {masterSpaces?.map(ms => (
                <option key={ms.id} value={ms.id}>
                  {ms.name}
                </option>
              ))}
            </select>
          </div>

          <div className={styles.detailField}>
            <div className={styles.detailLabel}>שטח</div>
            <div className={styles.detailValue}>
              {selectedData.estimated_size_sqm?.toFixed(1) || '—'} מ&quot;ר
            </div>
          </div>

          <div className={styles.detailField}>
            <div className={styles.detailLabel}>ביטחון</div>
            <ConfidenceBadge confidence={selectedData.confidence || 0} />
          </div>

          <div className={styles.detailActions}>
            <button
              className={styles.detailDeleteButton}
              onClick={() => onDeleteShape?.(selectedShape)}
            >
              <Trash size={14} />
              מחק
            </button>
          </div>
        </div>
      ) : (
        <div className={styles.noSelection}>לחץ על חלל לעריכה</div>
      )}

      {/* Shape list header */}
      <div className={styles.sidebarHeader}>
        {allShapes.length} חללים זוהו &middot; {includedCount} נבחרו
      </div>

      {/* Shape list */}
      <div className={styles.shapeList}>
        {allShapes.map((shape, idx) => {
          const data = shape.spaceData;
          if (!data) return null;
          const isActive = shape === selectedShape;
          const color = STATUS_COLORS[data.status] || STATUS_COLORS.new;

          return (
            <div
              key={idx}
              className={`${styles.shapeListItem} ${isActive ? styles.active : ''}`}
              onClick={() => onSelectShape?.(shape)}
            >
              <div
                className={styles.shapeListCheckbox}
                style={data.included ? { background: color, borderColor: color } : {}}
                onClick={e => {
                  e.stopPropagation();
                  onToggleInclude?.(shape);
                }}
              >
                {data.included && <Check size={12} weight="bold" color="#fff" />}
              </div>
              <div className={styles.shapeListDot} style={{ background: color }} />
              <div className={styles.shapeListName}>
                {data.suggested_name_he || data.suggested_name || `חלל ${idx + 1}`}
              </div>
              <div className={styles.shapeListSize}>
                {data.estimated_size_sqm?.toFixed(1) || '—'}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
